import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
  HostListener,
  HostBinding,
  ElementRef,
  AfterViewInit,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { PowerBIReportEmbedComponent } from 'powerbi-client-angular';
import { IReportEmbedConfiguration, models, service } from 'powerbi-client';
import { IframeDialogComponent } from '../iframe-dialog/iframe-dialog.component';

@Component({
  selector: 'app-iframe-viewer',
  templateUrl: './iframe-viewer.component.html',
  styleUrls: ['./iframe-viewer.component.scss'],
})
export class IframeViewerComponent implements OnInit, OnChanges, OnDestroy, AfterViewInit {
  @Input() url: string = '';
  @Input() token: string = '';
  @Input() showZoom = false;
  @Input() classContainer = 'powerbi-container';
  @Input() isActive: boolean = true;
  @Input() maxHeight: number | string | null = null;
  @HostBinding('class.iframe-viewer-constrained') get isConstrained(): boolean {
    return this.maxHeight != null && this.maxHeight !== '';
  }
  @Output() renderCompleted = new EventEmitter<void>();
  @Output() loadError = new EventEmitter<void>();

  private resizeTimeoutId: any;
  private reportEmbedded: boolean = false;
  private containerDimensions: { width: number; height: number; } = { width: 0, height: 0 };
  private isInternalResizeEvent: boolean = false;
  private pendingTimeoutIds: any[] = [];

  @ViewChild(PowerBIReportEmbedComponent)
  private powerBiReportComponent!: PowerBIReportEmbedComponent;

  @ViewChild('powerBiContainer', { read: ElementRef })
  private powerBiContainer!: ElementRef<HTMLDivElement>;

  reportConfig: IReportEmbedConfiguration = {
    type: 'report',
    embedUrl: '',
    tokenType: models.TokenType.Embed,
    accessToken: '',
    settings: {
      filterPaneEnabled: false,
      navContentPaneEnabled: true,
      layoutType: models.LayoutType.Custom,
      customLayout: {
        displayOption: models.DisplayOption.FitToWidth,
      },
    },
  };

  constructor(public dialog: MatDialog) { }

  ngOnInit(): void {
  }

  ngAfterViewInit(): void {
    setTimeout(() => {
      this.calculateAndUpdateDimensions();
    }, 100);
  }

  ngOnDestroy(): void {
    if (this.resizeTimeoutId) {
      clearTimeout(this.resizeTimeoutId);
    }
    this.pendingTimeoutIds.forEach(id => clearTimeout(id));
    this.pendingTimeoutIds = [];
  }

  @HostListener('window:resize', ['$event'])
  onWindowResize(event: any) {

    if (!this.isActive) {
      return;
    }

    if (this.isInternalResizeEvent) {
      this.isInternalResizeEvent = false;
      return;
    }

    if (this.resizeTimeoutId) {
      clearTimeout(this.resizeTimeoutId);
    }

    this.resizeTimeoutId = setTimeout(() => {
      this.triggerResponsiveResize();
    }, 250); // 250ms debounce
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['url']?.currentValue && changes['token']?.currentValue) {
      this.reportConfig = {
        ...this.reportConfig,
        embedUrl: this.url,
        accessToken: this.token,
      };
    }
  }

  openDialog(): void {
    this.dialog.open(IframeDialogComponent, {
      width: '80vw',
      height: '80vh',
      data: { url: this.url, token: this.token },
      disableClose: true,
      panelClass: 'full-screen-modal',
    });
  }
  onReportLoad() {

    this.reportEmbedded = true;
    this.pendingTimeoutIds.push(setTimeout(() => {
      this.calculateAndUpdateDimensions();
    }, 500));
  }

  onReportRender() {

    this.renderCompleted.emit();

    this.pendingTimeoutIds.push(setTimeout(() => {
      this.calculateAndUpdateDimensions();
    }, 200));
  }

  onReportError(event: any) {
    console.error('Error in report:', event.detail);
    this.loadError.emit();
  }


  private calculateAndUpdateDimensions(): void {
    try {
      if (!this.isActive) {
        return;
      }
      if (!this.reportEmbedded || !this.powerBiReportComponent) {

        return;
      }


      const dimensions = this.getOptimalDimensions();


      if (
        dimensions.width > 0 &&
        dimensions.height > 0 &&
        (dimensions.width !== this.containerDimensions.width ||
          dimensions.height !== this.containerDimensions.height)
      ) {

        this.containerDimensions = dimensions;
        this.updateContainerSize(dimensions);
        this.resizePowerBIReport(dimensions);
      }
    } catch (error) {
      console.warn('Error calculating dimensions:', error);
    }
  }

  private getOptimalDimensions(): { width: number; height: number; } {
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;


    const headerHeight = 64;  // App header
    const actionButtonsHeight = 60; // Action buttons (when present)
    const tabsHeight = 48; // Dashboard tabs (when present)
    const paddingBuffer = 40; // General padding and margins

    // Check if action buttons are present
    const hasActionButtons = document.querySelector('.action-buttons-container') !== null;

    // Check if tabs are present
    const hasTabs = document.querySelector('.tabs-container') !== null;

    // Calculate available height
    let availableHeight = viewportHeight - headerHeight - paddingBuffer;

    if (hasActionButtons) {
      availableHeight -= actionButtonsHeight;
    }

    if (hasTabs) {
      availableHeight -= tabsHeight;
    }

    // Calculate available width (accounting for potential sidebars)
    const availableWidth = viewportWidth - 40; // Base padding

    let height = Math.max(availableHeight, 500);
    if (this.maxHeight != null && this.maxHeight !== '') {
      const parsed =
        typeof this.maxHeight === 'string'
          ? parseInt(this.maxHeight.replace(/\D/g, ''), 10) || 400
          : this.maxHeight;
      height = Math.min(height, Math.max(parsed, 300));
    }

    return {
      width: Math.max(availableWidth, 800), // Minimum width
      height,
    };
  }


  private updateContainerSize(dimensions: { width: number; height: number; }): void {
    try {
      if (this.powerBiContainer?.nativeElement) {
        const container = this.powerBiContainer.nativeElement;
        container.style.width = `${dimensions.width}px`;
        container.style.height = `${dimensions.height}px`;
        container.style.maxWidth = '100%';
        container.style.maxHeight = '100%';
      }


      const iframeContainer = document.querySelector('.iframe-container') as HTMLElement;
      if (iframeContainer) {
        iframeContainer.style.height = `${dimensions.height}px`;
      }
    } catch (error) {
      console.warn('Error updating container size:', error);
    }
  }


  private resizePowerBIReport(dimensions: { width: number; height: number; }): void {
    try {

      const component = this.powerBiReportComponent as any;

      if (component && component.getReport) {
        const report = component.getReport();
        if (report && report.resize) {
          report.resize(dimensions).then(() => {

          }).catch((error: any) => {
            console.warn('Power BI API resize failed, using fallback:', error);
          });
        }
      }
    } catch (error) {

    }
  }


  private triggerResponsiveResize(): void {
    this.calculateAndUpdateDimensions();
  }

  private getIframeElement(): HTMLIFrameElement | null {
    const iframes = document.querySelectorAll('iframe');
    return iframes.length > 0 ? iframes[0] : null;
  }
}
